// ---------------------------------------------------------------------------
// What the choice on a product is CALLED, wherever that has to be read.
//
// The till says "اختر الحجم". The order line says "الحجم: كبير". The receipt and
// the butcher's ticket say the same. Four surfaces, one rule, and it lives
// here so that a product sold as a SIZE cannot be printed as a TYPE by
// whichever of them was written last.
//
// NULL MEANS TYPE. Every product that had variants before options existed
// carries no label, and so does every sale, ticket, held cart and order line
// written against one. Rather than backfill the shop's history with a word
// that was already the default, the default is applied HERE, once, at the
// moment somebody reads it.
//
// TWO SOURCES, AND THEY ARE NOT INTERCHANGEABLE:
//
//   a PRODUCT carries the label it has TODAY - that is what the till shows
//   while the cashier is choosing;
//
//   a DOCUMENT carries the label it was written with - that is what a receipt
//   reprinted next year shows, even if the owner has since decided the shop
//   calls it something else.
//
// Both arrive here as a pair of strings, so neither can be quietly used in
// place of the other by accident: the caller has to say which pair it is
// passing.
// ---------------------------------------------------------------------------

import type { Locale } from '../types';

/// A label as it is held: both languages, or nothing at all.
export interface OptionLabelPair {
  ar: string | null | undefined;
  en: string | null | undefined;
}

/// What a choice is called when the product does not say.
///
/// NOT in the dictionaries, deliberately. This is not a piece of interface
/// text that happens to need translating - it is the VALUE a product would
/// have stored if anybody had asked it, and the form writes exactly these two
/// strings when the owner picks the Type preset. Keeping it in one place is
/// what stops the word on the receipt drifting from the word in the dropdown.
export const DEFAULT_OPTION_LABEL = { ar: 'النوع', en: 'Type' } as const;

/// The words the form offers before the owner types their own.
///
/// A convenience on the way in, and nothing more: the product stores the two
/// strings themselves, so "درجة الاستواء" needs no entry here, no enum value
/// and no migration to become as ordinary as "الحجم".
export const OPTION_LABEL_PRESETS: ReadonlyArray<{ ar: string; en: string }> = [
  DEFAULT_OPTION_LABEL,
  { ar: 'الحجم', en: 'Size' },
  { ar: 'النكهة', en: 'Flavor' },
];

/// The word to use, in one language.
///
/// `locale` is the language the READER is in, which on paper is the language
/// the receipt is set to print in rather than the one the screen happens to
/// be showing.
export function optionLabelText(pair: OptionLabelPair, locale: Locale): string {
  const ar = pair.ar?.trim() ?? '';
  const en = pair.en?.trim() ?? '';

  // Both or neither. A half-filled pair cannot come through the form or the
  // API, but a stale offline catalogue is not something to crash over.
  const chosen = ar && en ? { ar, en } : DEFAULT_OPTION_LABEL;
  return locale === 'ar' ? chosen.ar : chosen.en;
}

/// "الحجم: كبير" / "Size: Large".
///
/// The one place this sentence is built. Returns null when there was no
/// choice to make, so a caller can render nothing rather than a stray colon.
export function optionLine(
  pair: OptionLabelPair,
  value: string | null | undefined,
  locale: Locale,
): string | null {
  const chosen = value?.trim();
  if (!chosen) return null;
  return `${optionLabelText(pair, locale)}: ${chosen}`;
}
